import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { FiHome, FiFolder, FiCode, FiUser, FiMail, FiLogOut, FiMenu, FiX, FiPlus, FiEdit2, FiTrash2, FiEye } from 'react-icons/fi';
import './Dashboard.css';
import API_BASE from '../../config';

const API_URL = `${API_BASE}/admin`;

const Dashboard = () => {
  const { admin, token, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState({ projects: 0, skills: 0, messages: 0, unreadMessages: 0 });

  useEffect(() => {
    fetchStats();
  }, [token]);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/dashboard`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) setStats(data.data);
    } catch (error) {
      console.error('Failed to fetch stats');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  const menuItems = [
    { id: 'overview', label: 'Overview', icon: <FiHome /> },
    { id: 'projects', label: 'Projects', icon: <FiFolder /> },
    { id: 'skills', label: 'Skills', icon: <FiCode /> },
    { id: 'profile', label: 'Profile', icon: <FiUser /> },
    { id: 'messages', label: 'Messages', icon: <FiMail />, badge: stats.unreadMessages },
  ];

  return (
    <div className="dashboard">
      <aside className={`dashboard-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h2>Admin Panel</h2>
          <button className="sidebar-close" onClick={() => setSidebarOpen(false)}><FiX /></button>
        </div>

        <nav className="sidebar-nav">
          {menuItems.map(item => (
            <button key={item.id} className={`nav-item ${activeTab === item.id ? 'active' : ''}`} onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}>
              {item.icon} <span>{item.label}</span>
              {item.badge > 0 && <span className="nav-badge">{item.badge}</span>}
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          <Link to="/" className="nav-item" target="_blank"><FiEye /> <span>View Site</span></Link>
          <button className="nav-item logout" onClick={handleLogout}><FiLogOut /> <span>Logout</span></button>
        </div>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-header">
          <button className="menu-toggle" onClick={() => setSidebarOpen(true)}><FiMenu /></button>
          <h1>{menuItems.find(m => m.id === activeTab)?.label}</h1>
          <div className="header-user">Welcome, {admin?.name || 'Admin'}</div>
        </header>

        <div className="dashboard-content">
          {activeTab === 'overview' && <OverviewTab stats={stats} setActiveTab={setActiveTab} />}
          {activeTab === 'projects' && <ProjectsTab token={token} />}
          {activeTab === 'skills' && <SkillsTab token={token} />}
          {activeTab === 'profile' && <ProfileTab token={token} />}
          {activeTab === 'messages' && <MessagesTab token={token} onUpdate={fetchStats} />}
        </div>
      </main>
    </div>
  );
};

const OverviewTab = ({ stats, setActiveTab }) => (
  <div className="overview-tab">
    <div className="stats-grid">
      <div className="stat-card" onClick={() => setActiveTab('projects')}><FiFolder className="stat-icon" /><div className="stat-info"><h3>{stats.projects}</h3><p>Projects</p></div></div>
      <div className="stat-card" onClick={() => setActiveTab('skills')}><FiCode className="stat-icon" /><div className="stat-info"><h3>{stats.skills}</h3><p>Skills</p></div></div>
      <div className="stat-card" onClick={() => setActiveTab('messages')}><FiMail className="stat-icon" /><div className="stat-info"><h3>{stats.messages}</h3><p>Messages</p></div></div>
      <div className="stat-card highlight" onClick={() => setActiveTab('messages')}><FiMail className="stat-icon" /><div className="stat-info"><h3>{stats.unreadMessages}</h3><p>Unread</p></div></div>
    </div>

    <div className="quick-actions">
      <h2>Quick Actions</h2>
      <div className="actions-grid">
        <button className="action-btn" onClick={() => setActiveTab('projects')}><FiPlus /> Add Project</button>
        <button className="action-btn" onClick={() => setActiveTab('skills')}><FiPlus /> Add Skill</button>
        <button className="action-btn" onClick={() => setActiveTab('profile')}><FiEdit2 /> Edit Profile</button>
        <button className="action-btn" onClick={() => setActiveTab('messages')}><FiMail /> View Messages</button>
      </div>
    </div>
  </div>
);

const ProjectsTab = ({ token }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({ title: '', description: '', features: '', technologies: '', github: '', live: '', featured: false });

  useEffect(() => { fetchProjects(); }, []);

  const fetchProjects = async () => {
    try {
      const res = await fetch(`${API_URL}/projects`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) setProjects(data.data);
    } catch (error) { console.error(error); }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      features: formData.features.split('\n').filter(f => f.trim()),
      technologies: formData.technologies.split(',').map(t => t.trim()).filter(t => t)
    };

    const url = editingProject ? `${API_URL}/projects/${editingProject._id}` : `${API_URL}/projects`;
    await fetch(url, {
      method: editingProject ? 'PUT' : 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    fetchProjects();
    resetForm();
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      title: project.title,
      description: project.description,
      features: project.features?.join('\n') || '',
      technologies: project.technologies?.join(', ') || '',
      github: project.github || '',
      live: project.live || '',
      featured: project.featured || false
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this project?')) return;
    await fetch(`${API_URL}/projects/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
    fetchProjects();
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingProject(null);
    setFormData({ title: '', description: '', features: '', technologies: '', github: '', live: '', featured: false });
  };

  return (
    <div className="tab-content">
      <div className="tab-header">
        <h2>Projects</h2>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>{showForm ? 'Cancel' : <><FiPlus /> Add</>}</button>
      </div>

      {showForm && (
        <form className="admin-form" onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group"><label>Title *</label><input type="text" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} required /></div>
            <div className="form-group"><label><input type="checkbox" checked={formData.featured} onChange={e => setFormData({...formData, featured: e.target.checked})} /> Featured</label></div>
          </div>
          <div className="form-group"><label>Description *</label><textarea rows="3" value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} required /></div>
          <div className="form-group"><label>Features (one per line)</label><textarea rows="4" value={formData.features} onChange={e => setFormData({...formData, features: e.target.value})} /></div>
          <div className="form-group"><label>Technologies (comma separated) *</label><input type="text" value={formData.technologies} onChange={e => setFormData({...formData, technologies: e.target.value})} required /></div>
          <div className="form-row">
            <div className="form-group"><label>GitHub URL</label><input type="url" value={formData.github} onChange={e => setFormData({...formData, github: e.target.value})} /></div>
            <div className="form-group"><label>Live URL</label><input type="url" value={formData.live} onChange={e => setFormData({...formData, live: e.target.value})} /></div>
          </div>
          <button type="submit" className="btn btn-primary">{editingProject ? 'Update' : 'Add'}</button>
        </form>
      )}

      <div className="items-list">
        {loading ? <p>Loading...</p> : projects.length === 0 ? <p>No projects yet</p> :
          projects.map(project => (
            <div key={project._id} className="list-item">
              <div className="item-info">
                <h3>{project.title} {project.featured && <span className="badge">Featured</span>}</h3>
                <p>{project.description?.substring(0, 100)}...</p>
                <div className="item-tags">{project.technologies?.map((t, i) => <span key={i} className="tag">{t}</span>)}</div>
              </div>
              <div className="item-actions">
                <button onClick={() => handleEdit(project)}><FiEdit2 /></button>
                <button onClick={() => handleDelete(project._id)} className="delete"><FiTrash2 /></button>
              </div>
            </div>
          ))
        }
      </div>
    </div>
  );
};

const SkillsTab = ({ token }) => {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSkill, setEditingSkill] = useState(null);
  const [formData, setFormData] = useState({ category: 'programmingLanguages', categoryTitle: 'Programming Languages', name: '', level: 80 });

  const categories = [
    { value: 'programmingLanguages', label: 'Programming Languages' },
    { value: 'backend', label: 'Backend' },
    { value: 'databases', label: 'Databases' },
    { value: 'tools', label: 'Tools' },
    { value: 'core', label: 'Core Knowledge' }
  ];

  useEffect(() => { fetchSkills(); }, []);

  const fetchSkills = async () => {
    try {
      const res = await fetch(`${API_URL}/skills`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) setSkills(data.data);
    } catch (error) { console.error(error); }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const url = editingSkill ? `${API_URL}/skills/${editingSkill._id}` : `${API_URL}/skills`;
    await fetch(url, {
      method: editingSkill ? 'PUT' : 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(formData)
    });
    fetchSkills();
    resetForm();
  };

  const handleEdit = (skill) => {
    setEditingSkill(skill);
    setFormData({ category: skill.category, categoryTitle: skill.categoryTitle, name: skill.name, level: skill.level });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this skill?')) return;
    await fetch(`${API_URL}/skills/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
    fetchSkills();
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingSkill(null);
    setFormData({ category: 'programmingLanguages', categoryTitle: 'Programming Languages', name: '', level: 80 });
  };

  const handleCategoryChange = (e) => {
    const cat = categories.find(c => c.value === e.target.value);
    setFormData({ ...formData, category: cat.value, categoryTitle: cat.label });
  };

  const groupedSkills = skills.reduce((acc, skill) => {
    if (!acc[skill.category]) acc[skill.category] = [];
    acc[skill.category].push(skill);
    return acc;
  }, {});

  return (
    <div className="tab-content">
      <div className="tab-header">
        <h2>Skills</h2>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>{showForm ? 'Cancel' : <><FiPlus /> Add</>}</button>
      </div>

      {showForm && (
        <form className="admin-form" onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group"><label>Category *</label><select value={formData.category} onChange={handleCategoryChange}>{categories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}</select></div>
            <div className="form-group"><label>Skill Name *</label><input type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required /></div>
          </div>
          <div className="form-group"><label>Level: {formData.level}%</label><input type="range" min="0" max="100" value={formData.level} onChange={e => setFormData({...formData, level: parseInt(e.target.value)})} /></div>
          <button type="submit" className="btn btn-primary">{editingSkill ? 'Update' : 'Add'}</button>
        </form>
      )}

      <div className="skills-sections">
        {loading ? <p>Loading...</p> : Object.keys(groupedSkills).length === 0 ? <p>No skills yet</p> :
          Object.entries(groupedSkills).map(([cat, catSkills]) => (
            <div key={cat} className="skill-section">
              <h3>{categories.find(c => c.value === cat)?.label || cat}</h3>
              <div className="items-list">
                {catSkills.map(skill => (
                  <div key={skill._id} className="list-item compact">
                    <div className="item-info"><h4>{skill.name}</h4><div className="skill-bar-mini"><div style={{ width: `${skill.level}%` }}></div></div><span>{skill.level}%</span></div>
                    <div className="item-actions">
                      <button onClick={() => handleEdit(skill)}><FiEdit2 /></button>
                      <button onClick={() => handleDelete(skill._id)} className="delete"><FiTrash2 /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        }
      </div>
    </div>
  );
};

const ProfileTab = ({ token }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => { fetchProfile(); }, []);

  const fetchProfile = async () => {
    try {
      const res = await fetch(`${API_URL}/profile`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) setProfile(data.data);
    } catch (error) { console.error(error); }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });
    
    try {
      const res = await fetch(`${API_URL}/profile`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: profile.name, title: profile.title, email: profile.email, phone: profile.phone,
          location: profile.location, github: profile.github, linkedin: profile.linkedin,
          website: profile.website, bio: profile.bio, education: profile.education, awards: profile.awards
        })
      });
      const data = await res.json();
      
      if (data.success) {
        setMessage({ type: 'success', text: 'Saved!' });
        setProfile(data.data);
      } else {
        setMessage({ type: 'error', text: 'Failed to save' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error saving' });
    }
    
    setSaving(false);
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  if (loading) return <p>Loading...</p>;
  if (!profile) return <p>Failed to load</p>;

  return (
    <div className="tab-content">
      <div className="tab-header"><h2>Profile</h2></div>

      {message.text && <div className={`alert ${message.type}`}>{message.text}</div>}

      <form className="admin-form" onSubmit={handleSubmit}>
        <h3>Personal Info</h3>
        <div className="form-row">
          <div className="form-group"><label>Name *</label><input type="text" value={profile.name || ''} onChange={e => setProfile({...profile, name: e.target.value})} required /></div>
          <div className="form-group"><label>Title *</label><input type="text" value={profile.title || ''} onChange={e => setProfile({...profile, title: e.target.value})} required /></div>
        </div>
        <div className="form-row">
          <div className="form-group"><label>Email *</label><input type="email" value={profile.email || ''} onChange={e => setProfile({...profile, email: e.target.value})} required /></div>
          <div className="form-group"><label>Phone</label><input type="text" value={profile.phone || ''} onChange={e => setProfile({...profile, phone: e.target.value})} /></div>
        </div>
        <div className="form-group"><label>Location</label><input type="text" value={profile.location || ''} onChange={e => setProfile({...profile, location: e.target.value})} /></div>
        <div className="form-row">
          <div className="form-group"><label>GitHub</label><input type="url" value={profile.github || ''} onChange={e => setProfile({...profile, github: e.target.value})} /></div>
          <div className="form-group"><label>LinkedIn</label><input type="url" value={profile.linkedin || ''} onChange={e => setProfile({...profile, linkedin: e.target.value})} /></div>
        </div>
        <div className="form-group"><label>Website</label><input type="url" value={profile.website || ''} onChange={e => setProfile({...profile, website: e.target.value})} /></div>
        <div className="form-group"><label>Bio</label><textarea rows="4" value={profile.bio || ''} onChange={e => setProfile({...profile, bio: e.target.value})} /></div>

        <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
      </form>
    </div>
  );
};

const MessagesTab = ({ token, onUpdate }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchMessages(); }, []);

  const fetchMessages = async () => {
    try {
      const res = await fetch(`${API_URL}/messages`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) setMessages(data.data);
    } catch (error) { console.error(error); }
    setLoading(false);
  };

  const markAsRead = async (id) => {
    await fetch(`${API_URL}/messages/${id}`, { method: 'PUT', headers: { 'Authorization': `Bearer ${token}` } });
    fetchMessages();
    onUpdate();
  };

  const deleteMessage = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    await fetch(`${API_URL}/messages/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
    fetchMessages();
    onUpdate();
  };

  return (
    <div className="tab-content">
      <div className="tab-header"><h2>Messages</h2></div>

      <div className="messages-list">
        {loading ? <p>Loading...</p> : messages.length === 0 ? <p>No messages</p> :
          messages.map(msg => (
            <div key={msg._id} className={`message-card ${!msg.isRead ? 'unread' : ''}`}>
              <div className="message-header">
                <div><h3>{msg.name}</h3><a href={`mailto:${msg.email}`}>{msg.email}</a></div>
                <span className="message-date">{new Date(msg.createdAt).toLocaleDateString()}</span>
              </div>
              <h4>{msg.subject}</h4>
              <p>{msg.message}</p>
              <div className="message-actions">
                {!msg.isRead && <button onClick={() => markAsRead(msg._id)} className="btn-sm">Mark Read</button>}
                <button onClick={() => deleteMessage(msg._id)} className="btn-sm delete">Delete</button>
              </div>
            </div>
          ))
        }
      </div>
    </div>
  );
};

export default Dashboard;
